/**
 * Reserved slugs for organization creation.
 *
 * RESERVED_SLUGS: hard-blocked. These collide with top-level routes in
 * app/ or with framework/SEO assets. Source of truth duplicated in the
 * provision_organization RPC (supabase/migrations/20260607000000_*.sql)
 * as a defense-in-depth measure.
 *
 * KNOWN_INSTITUTION_SLUGS: not blocked. Used to flag the resulting
 * organization for manual review when the slug matches but the creator's
 * email domain doesn't plausibly correspond. Provisioning still succeeds;
 * we set slug_review_status = 'flagged' so a human can verify.
 */

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  // App route segments (must match top-level /app/* directories)
  'admin',
  'api',
  'auth',
  'for',
  'get-app',
  'in',
  'join',
  'login',
  'logout',
  'offline',
  'onboarding',
  'payment',
  'policies',
  'pricing',
  'profile',
  'requests',
  'settings',
  'setup',
  'signup-incomplete',
  'forgot-password',
  'start',
  // System / reserved
  'default',
  'www',
  'help',
  'docs',
  'support',
  'about',
  'status',
  'blog',
  'app',
  'mail',
  'email',
  'public',
  'static',
  'assets',
  'favicon',
  'robots',
  'sitemap',
  'sw',
  'manifest',
  'opengraph',
  'apple-icon',
  'icon',
  'globals',
  'not-found',
  'error',
])

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug.toLowerCase())
}

/**
 * Lightweight institution-name heuristic. When the requested slug matches one
 * of these AND the creator's email domain does not contain the slug,
 * provisioning still succeeds but slug_review_status is set to 'flagged'
 * for manual review. Not a security control — a noise-reduction signal.
 */
const KNOWN_INSTITUTION_SLUGS: ReadonlySet<string> = new Set([
  'harvard',
  'stanford',
  'mit',
  'yale',
  'princeton',
  'columbia',
  'cornell',
  'dartmouth',
  'upenn',
  'brown',
  'caltech',
  'berkeley',
  'ucla',
  'oxford',
  'cambridge',
  'lse',
  'imperial',
  'eth',
  'epfl',
  'insead',
  'wharton',
  'kellogg',
  'sloan',
  'booth',
  'hbs',
  'gsb',
  'iese',
  'iesa',
  'iae',
  'utdt',
])

export function computeSlugReviewStatus(slug: string, email: string): 'auto_approved' | 'flagged' {
  const normalizedSlug = slug.toLowerCase()
  if (!KNOWN_INSTITUTION_SLUGS.has(normalizedSlug)) {
    return 'auto_approved'
  }
  const domain = email.split('@')[1]?.toLowerCase() ?? ''
  // If the email domain plausibly relates to the slug, accept.
  // e.g. slug "harvard" + email "@harvard.edu" → auto_approved.
  if (domain.includes(normalizedSlug)) {
    return 'auto_approved'
  }
  return 'flagged'
}

/**
 * Letters that NFD does not decompose (a stroke or a ligature is part of the
 * letter, not a combining mark), so they would otherwise become a hyphen:
 * "Łódź" → "odz". Lower case only: the fold lower-cases first.
 */
const TRANSLITERATION: Record<string, string> = {
  ø: 'o',
  æ: 'ae',
  œ: 'oe',
  ß: 'ss',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ħ: 'h',
  ı: 'i',
}
const TRANSLITERATION_RE = new RegExp(`[${Object.keys(TRANSLITERATION).join('')}]`, 'g')

/** Lower case with accents folded to their base letter: "Cañaverales" → "canaverales". */
function foldAccents(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(TRANSLITERATION_RE, (ch) => TRANSLITERATION[ch])
}

export function normalizeSlug(input: string): string {
  return foldAccents(input)
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * A slug while it is being typed (the new-institution wizard, gate audit S-7):
 * lower case, accents folded, anything that is not a letter, a digit or a
 * hyphen turned into a hyphen. Unlike `normalizeSlug` it keeps a hyphen at
 * the END, because that is the one the person just typed: "colegio-" on its
 * way to "colegio-canaverales". A hyphen at the start still goes, and runs
 * collapse. Finish with `normalizeSlug` when the field loses focus.
 */
export function typingSlug(input: string): string {
  return foldAccents(input)
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
}

/** The slug a name suggests, within the 40-character limit: "Colegio Cañaverales" → "colegio-canaverales". */
export function slugFromName(name: string): string {
  return normalizeSlug(name).slice(0, 40).replace(/-+$/, '')
}

export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/
